import { useEffect, useMemo, useState } from "react";
import { api, API, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import {
  FileText, Plus, Download, RefreshCcw, ShieldCheck, Send, Lock, X,
  ChevronRight, Trash2, Unlock, Clipboard,
} from "lucide-react";

/*
 * Reports — automated report assembly for ISC, funding programs, external
 * agencies and internal governance. Data is pre-filled from live nation
 * dockets, sessions, surveys, and financials via each template's pull specs.
 *
 * Flow: choose template → open run for a period → review pre-filled numbers
 * and lists, fill narrative sections, refresh pulls if needed → submit for
 * review → sign-off chain → export CSV or JSON.
 */

const STATUS_STYLE = {
  draft:            { color: "#a6adc8", label: "Draft"           },
  in_review:        { color: "#d4b876", label: "In review"       },
  partially_signed: { color: "#d4b876", label: "Partially signed" },
  signed:           { color: "#89b4fa", label: "Signed"          },
  submitted:        { color: "#89b4fa", label: "Submitted"       },
};

const currency = (n) => n === "" || n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

export default function Reports() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [openRun, setOpenRun] = useState(null); // run id
  const [creating, setCreating] = useState(null); // template obj

  const canSignoff = user && (user.is_super_admin || user.is_owner ||
    ["chief","deputy_chief","head_councillor","councillor","executive_director"].includes(user.role));

  const load = async () => {
    const [tpls, rs, dash] = await Promise.all([
      api.get("/reports/templates").then(r=>r.data).catch(()=>[]),
      api.get("/reports/runs").then(r=>r.data).catch(()=>[]),
      api.get("/reports/dashboard").then(r=>r.data).catch(()=>null),
    ]);
    setTemplates(tpls);
    setRuns(rs);
    setDashboard(dash);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">Reporting workbench</div>
          <h1 className="font-display text-4xl mt-2 leading-tight">Reports</h1>
          <p className="text-white/55 mt-3 max-w-xl text-sm leading-relaxed">
            Assemble ISC, funding-program and internal reports from live nation data. Every figure is
            pulled at export time; narrative sits alongside the numbers; sign-off is on the record.
          </p>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <Kpi label="Active templates" value={dashboard?.active_templates ?? "—"} testid="kpi-templates"/>
        <Kpi label="Drafts" value={dashboard?.drafts ?? "—"} color="#a6adc8" testid="kpi-drafts"/>
        <Kpi label="Awaiting sign-off" value={dashboard?.in_review ?? "—"} color="#d4b876" testid="kpi-in-review"/>
        <Kpi label="Signed & archived" value={dashboard?.signed ?? "—"} color="#89b4fa" testid="kpi-signed"/>
      </div>

      {/* Templates */}
      <section className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <div className="label-mono">Report templates</div>
          <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
            {templates.length} active
          </span>
        </div>
        {templates.length === 0 ? (
          <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
            <div className="font-display text-xl">No templates yet.</div>
            <div className="text-white/50 text-sm mt-2">
              Ready-to-run ISC, CMHC and internal templates ship with the portal. If none appear here,
              contact the executive administrator.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => (
              <div key={t.id} className="border border-white/8 bg-white/[0.015] p-5 flex flex-col" data-testid={`template-${t.id}`}>
                <div className="flex items-center justify-between">
                  <span className="tag" style={{color: "#89b4fa"}}>{t.agency}</span>
                  <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{t.frequency}</span>
                </div>
                <div className="font-display text-lg mt-3 leading-tight">{t.name}</div>
                {t.program && <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">{t.program}</div>}
                <div className="text-white/55 text-xs mt-3 leading-relaxed">
                  {t.sections.length} sections · sign-off by {(t.signoff_chain || []).map(r => ROLE_LABEL[r] || r).join(" → ")}
                </div>
                <div className="mt-4 pt-4 border-t border-white/8 flex justify-end">
                  <button onClick={()=>setCreating(t)} className="btn-cta primary text-xs" data-testid={`open-run-${t.id}`}>
                    <Plus size={12}/> Open a report run
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Runs */}
      <section className="mt-12">
        <div className="flex items-baseline justify-between mb-4">
          <div className="label-mono">Report runs</div>
          <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
            {runs.length} on file
          </span>
        </div>
        {runs.length === 0 ? (
          <div className="border border-white/8 bg-white/[0.015] p-8 text-center">
            <div className="font-display text-xl">Nothing filed yet.</div>
            <div className="text-white/50 text-sm mt-2">
              Open a template above to start assembling a report. Once you submit it, it appears here.
            </div>
          </div>
        ) : (
          <div className="border border-white/8">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03]">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-white/50">
                  <th className="px-4 py-3">Report</th>
                  <th className="px-4 py-3">Agency</th>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Updated</th>
                  <th className="px-4 py-3"/>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const st = STATUS_STYLE[r.status] || {color: "#a6adc8", label: r.status};
                  return (
                    <tr key={r.id} className="border-t border-white/8 hover:bg-white/[0.02] cursor-pointer" onClick={()=>setOpenRun(r.id)} data-testid={`run-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-display truncate max-w-xs">{r.template_name}</div>
                        {r.notes && <div className="font-mono text-[10px] text-white/40 truncate max-w-xs">{r.notes}</div>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70">{r.template_agency}</td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70">{r.period_label}</td>
                      <td className="px-4 py-3">
                        <span className="tag" style={{color: st.color}}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-white/70">{r.created_by_name}</td>
                      <td className="px-4 py-3 font-mono text-[10px] text-white/50">
                        {new Date(r.updated_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3"><ChevronRight size={14} className="text-white/30"/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating && (
        <RunOpenModal template={creating} onClose={()=>setCreating(null)} onCreated={(id)=>{ setCreating(null); setOpenRun(id); load(); }} />
      )}
      {openRun && (
        <RunEditor
          runId={openRun}
          canSignoff={canSignoff}
          onClose={()=>{ setOpenRun(null); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="border border-white/8 bg-white/[0.015] p-5 corner-tick" data-testid={testid}>
      <div className="font-display text-3xl" style={{color: color || "#cdd6f4"}}>{value}</div>
      <div className="label-mono mt-2">{label}</div>
    </div>
  );
}

function RunOpenModal({ template, onClose, onCreated }) {
  const [form, setForm] = useState(() => defaultPeriod(template.frequency));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/reports/runs", {
        template_id: template.id,
        period_label: form.period_label,
        period_start: new Date(form.period_start).toISOString(),
        period_end: new Date(form.period_end).toISOString(),
        notes: form.notes || null,
      });
      toast.success("Report run created. Live data pre-filled.");
      onCreated(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to open run");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit}
        className="max-w-lg mx-auto frost-glass p-6 my-16 space-y-4 border border-white/12" data-testid="run-open-modal">
        <div className="label-mono">Open a report run</div>
        <div>
          <div className="font-display text-2xl leading-tight">{template.name}</div>
          <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest mt-1">
            {template.agency} · {template.frequency}
          </div>
        </div>
        <label className="block">
          <div className="label-mono mb-1">Period label</div>
          <input required value={form.period_label} onChange={e=>setForm({...form, period_label: e.target.value})}
            placeholder="e.g. Q1 2026" className="w-full px-3 py-2 text-sm" data-testid="run-period-label"/>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="label-mono mb-1">Period start</div>
            <input required type="date" value={form.period_start}
              onChange={e=>setForm({...form, period_start: e.target.value})}
              className="w-full px-3 py-2 text-sm" data-testid="run-period-start"/>
          </label>
          <label className="block">
            <div className="label-mono mb-1">Period end</div>
            <input required type="date" value={form.period_end}
              onChange={e=>setForm({...form, period_end: e.target.value})}
              className="w-full px-3 py-2 text-sm" data-testid="run-period-end"/>
          </label>
        </div>
        <label className="block">
          <div className="label-mono mb-1">Notes (optional)</div>
          <textarea rows={2} value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})}
            className="w-full px-3 py-2 text-sm"/>
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t border-white/8">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={busy} className="btn-cta primary" data-testid="run-open-submit">
            <FileText size={12}/> {busy?"Opening…":"Open run"}
          </button>
        </div>
      </form>
    </div>
  );
}

function defaultPeriod(frequency) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  let start, end, label;
  if (frequency === "quarterly") {
    const q = Math.floor(m/3);
    start = new Date(Date.UTC(y, q*3, 1));
    end = new Date(Date.UTC(y, q*3+3, 0));
    label = `Q${q+1} ${y}`;
  } else if (frequency === "annual") {
    start = new Date(Date.UTC(y, 0, 1));
    end = new Date(Date.UTC(y, 11, 31));
    label = `FY ${y}`;
  } else if (frequency === "monthly") {
    start = new Date(Date.UTC(y, m, 1));
    end = new Date(Date.UTC(y, m+1, 0));
    label = start.toLocaleDateString("en-CA", { month: "short", year: "numeric" });
  } else {
    start = new Date(Date.UTC(y, m, 1));
    end = new Date(Date.UTC(y, m+1, 0));
    label = `${start.toLocaleDateString("en-CA", { month:"short", year:"numeric" })}`;
  }
  const iso = (d) => d.toISOString().slice(0,10);
  return { period_label: label, period_start: iso(start), period_end: iso(end), notes: "" };
}

function RunEditor({ runId, canSignoff, onClose }) {
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  const load = () => api.get(`/reports/runs/${runId}`).then(r => setRun(r.data)).catch(()=>{});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [runId]);

  if (!run) return null;
  const locked = ["signed", "submitted"].includes(run.status);
  const st = STATUS_STYLE[run.status] || {color: "#a6adc8", label: run.status};

  const setAnswer = (sid, value) => {
    setDirty(d => ({ ...d, [sid]: value }));
    setRun(r => ({ ...r, answers: r.answers.map(a => a.section_id === sid ? { ...a, value } : a) }));
  };

  const save = async () => {
    if (Object.keys(dirty).length === 0) return;
    setSaving(true);
    try {
      const answers = Object.entries(dirty).map(([section_id, value]) => ({ section_id, value }));
      await api.patch(`/reports/runs/${runId}`, { answers });
      setDirty({});
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const refresh = async () => {
    try {
      const { data } = await api.post(`/reports/runs/${runId}/refresh`);
      toast.success(`Refreshed ${data.refreshed_sections} auto-pull section${data.refreshed_sections===1?"":"s"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
  };

  const submitForReview = async () => {
    if (Object.keys(dirty).length) await save();
    try {
      await api.post(`/reports/runs/${runId}/submit-for-review`);
      toast.success("Submitted for review");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot submit"); }
  };

  const signoff = async () => {
    try {
      const { data } = await api.post(`/reports/runs/${runId}/signoff`);
      toast.success(data.status === "signed" ? "Report fully signed" : "Sign-off recorded");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sign-off failed"); }
  };

  const reopen = async () => {
    const ok = await confirm({ title: "Reopen this signed report?", message: "Reopening clears all sign-offs. The report returns to draft status.", confirmLabel: "Reopen" });
    if (!ok) return;
    try {
      await api.post(`/reports/runs/${runId}/reopen`);
      toast.success("Reopened as draft");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cannot reopen"); }
  };

  const removeRun = async () => {
    const ok = await confirm({ title: "Delete report run?", destructive: true, message: "This report is not yet signed. Deletion is permanent.", confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api.delete(`/reports/runs/${runId}`);
      toast.success("Removed");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="max-w-4xl mx-auto frost-glass border border-white/12 my-8 p-8" data-testid="run-editor">
        {/* Header */}
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="label-mono">{run.template_agency} · {run.period_label}</div>
            <h2 className="font-display text-3xl mt-2 leading-tight">{run.template_name}</h2>
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              <span className="tag" style={{color: st.color}}>{st.label}</span>
              <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">
                Owner {run.created_by_name} · Updated {new Date(run.updated_at).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!locked && (
              <>
                <button onClick={refresh} className="btn-cta text-xs" data-testid="run-refresh">
                  <RefreshCcw size={12}/> Refresh pulls
                </button>
                <button onClick={save} disabled={saving || Object.keys(dirty).length === 0} className="btn-cta text-xs" data-testid="run-save">
                  <Clipboard size={12}/> {saving ? "Saving…" : Object.keys(dirty).length ? `Save (${Object.keys(dirty).length})` : "Saved"}
                </button>
              </>
            )}
            <a href={`${API}/reports/runs/${runId}/export.csv`} download className="btn-cta text-xs" data-testid="run-export-csv">
              <Download size={12}/> CSV
            </a>
            <a href={`${API}/reports/runs/${runId}/export.json`} download className="btn-cta text-xs" data-testid="run-export-json">
              <Download size={12}/> JSON
            </a>
            <button onClick={onClose} className="btn-cta text-xs" data-testid="run-close">
              <X size={12}/>
            </button>
          </div>
        </div>

        {/* Sign-off chain */}
        <div className="border border-white/8 bg-white/[0.015] p-4 mt-6">
          <div className="label-mono mb-2">Sign-off chain</div>
          <div className="flex flex-wrap gap-3">
            {(run.template_signoff_chain || []).map((role, i) => {
              const s = (run.signoffs || []).find(x => x.role === role);
              return (
                <div key={role+i} className="flex items-center gap-2 font-mono text-xs">
                  <span className={s ? "text-[color:var(--cobalt)]" : "text-white/40"}>
                    {ROLE_LABEL[role] || role}
                  </span>
                  {s ? (
                    <span className="text-white/60">✓ {s.name} · {new Date(s.at).toLocaleDateString()}</span>
                  ) : (
                    <span className="text-white/40">pending</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sections */}
        <div className="mt-8 space-y-5">
          {run.answers.map((a, i) => (
            <SectionRow key={a.section_id} idx={i} answer={a} locked={locked}
              onChange={(v)=>setAnswer(a.section_id, v)}/>
          ))}
        </div>

        {/* Actions */}
        <div className="mt-8 pt-6 border-t border-white/8 flex flex-wrap gap-2 justify-end">
          {!locked && run.status === "draft" && (
            <button onClick={submitForReview} className="btn-cta primary text-xs" data-testid="run-submit-review">
              <Send size={12}/> Submit for review
            </button>
          )}
          {(run.status === "in_review" || run.status === "partially_signed") && canSignoff && (
            <button onClick={signoff} className="btn-cta primary text-xs" data-testid="run-signoff">
              <ShieldCheck size={12}/> Sign this report
            </button>
          )}
          {run.status === "signed" && (user.is_super_admin || user.role === "chief" || user.role === "executive_director") && (
            <button onClick={reopen} className="btn-cta text-xs" data-testid="run-reopen">
              <Unlock size={12}/> Reopen
            </button>
          )}
          {!locked && (
            <button onClick={removeRun} className="btn-cta danger text-xs" data-testid="run-delete">
              <Trash2 size={12}/> Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionRow({ idx, answer, locked, onChange }) {
  const isAutoList = Array.isArray(answer.auto_data);
  const isCurrency = answer.kind === "currency";
  const val = answer.value ?? "";
  return (
    <div className="border border-white/8 bg-white/[0.015] p-5" data-testid={`section-${answer.section_id}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">
            {String(idx+1).padStart(2,"0")} · {answer.kind}{answer.required ? " · required" : ""}
          </span>
          <div className="font-display text-lg mt-1">{answer.label}</div>
        </div>
        {answer.auto_ran_at && (
          <span className="font-mono text-[10px] text-[color:var(--cobalt)]/80 uppercase tracking-widest">
            auto · pulled {new Date(answer.auto_ran_at).toLocaleTimeString()}
          </span>
        )}
      </div>
      {answer.prompt && <div className="text-white/55 text-sm mt-2 leading-relaxed">{answer.prompt}</div>}

      {isAutoList ? (
        <div className="mt-4 border border-white/8">
          {answer.auto_data.length === 0 ? (
            <div className="p-4 font-mono text-xs text-white/40">No matching records in this period.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03] text-white/50 uppercase tracking-widest">
                <tr>
                  <th className="px-3 py-2 text-left font-mono text-[10px]">Title</th>
                  <th className="px-3 py-2 text-left font-mono text-[10px]">Status</th>
                  <th className="px-3 py-2 text-right font-mono text-[10px]">Allocated</th>
                  <th className="px-3 py-2 text-right font-mono text-[10px]">Spent</th>
                </tr>
              </thead>
              <tbody>
                {answer.auto_data.map((row, j) => (
                  <tr key={j} className="border-t border-white/8">
                    <td className="px-3 py-2 text-white/80">{row.title || row.id}</td>
                    <td className="px-3 py-2 font-mono text-white/60">{row.status || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono text-white/70">{currency(row.budget_allocated)}</td>
                    <td className="px-3 py-2 text-right font-mono text-white/70">{currency(row.budget_spent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="p-3 border-t border-white/8 bg-white/[0.02]">
            <div className="label-mono mb-1">Narrative</div>
            <textarea rows={2} value={val} disabled={locked} onChange={e=>onChange(e.target.value)}
              placeholder="Contextual notes for the reviewer / agency…"
              className="w-full px-3 py-2 text-sm bg-transparent" data-testid={`answer-${answer.section_id}`}/>
          </div>
        </div>
      ) : answer.kind === "text" ? (
        <textarea rows={3} value={val} disabled={locked} onChange={e=>onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm mt-3" data-testid={`answer-${answer.section_id}`}/>
      ) : (
        <input
          type={answer.kind === "number" || answer.kind === "currency" ? "number" : "text"}
          value={val} disabled={locked} onChange={e=>onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm mt-3" data-testid={`answer-${answer.section_id}`}
        />
      )}
      {isCurrency && val && (
        <div className="font-mono text-[10px] text-white/50 mt-1 uppercase tracking-widest">{currency(val)}</div>
      )}
    </div>
  );
}
